import type React from 'react';
import { useMemo, useState } from 'react';

import { keepPreviousData } from '@tanstack/react-query';
import { useGetEvents } from '@vlabs/api/artemy/client';
import type { FaceId } from '@vlabs/api/artemy/model';
import { ShowEventsByFaceIdLinkPoint3 } from '@vlabs/monokit/features/show-events-by-face/ShowEventsByFaceIdLink.point3';
import { EventCardPoint3 } from '@vlabs/monokit/shared/ui/event-card/EventCard.point3';
import { Button } from '@vlabs/uikit/components/controls/button/Button';
import { useTranslation } from 'react-i18next';

import st from './LatestFaceEventsSection.module.sass';
import { PageSection } from '../../page-section';

interface LatestFaceEventsSectionProps {
  faceId: FaceId;
}

const EVENTS_PAGE_SIZE = 4;

function LatestFaceEventsSection({
  faceId,
}: LatestFaceEventsSectionProps): React.JSX.Element {
  const { t } = useTranslation();
  const [pageSize, setPageSize] = useState<number>(EVENTS_PAGE_SIZE);
  const getEvents = useGetEvents({ top_similar_object_ids: [faceId], page_size: pageSize, target: ['event_id'] }, {
    query: {
      placeholderData: keepPreviousData,
    },
  });

  const events = useMemo(() => getEvents.data?.events?.map((event) => {
    if (!event.event_id) return undefined;
    return (
      <EventCardPoint3
        eventId={event.event_id}
        key={event.event_id}
      />
    );
  }).filter((e) => e !== undefined) ?? [], [getEvents.data?.events]);

  const showMore = useMemo(() => {
    return (
      <Button
        disabled={!getEvents.isFetched}
        kind="primary"
        onClick={() => setPageSize((prevPageSize) => prevPageSize + EVENTS_PAGE_SIZE)}
        size="small"
        variant="outlined"
      >
        {t('face-page:latest_face_events.show_more')}
      </Button>
    );
  }, [t, getEvents.isFetched]);

  return (
    <PageSection
      actions={events.length !== 0 ? <ShowEventsByFaceIdLinkPoint3 faceId={faceId} /> : undefined}
      title={t('face-page:latest_face_events.title')}
    >
      {events.length > 0 && (
        <div className={st.EventsContainer}>
          <div className={st.Events}>{events}</div>
          {pageSize > events.length && showMore}
        </div>
      )}
      {events.length === 0 && <div className={st.NoEvents}>{t('face-page:latest_face_events.no_events')}</div> }
    </PageSection>
  );
}

export { LatestFaceEventsSection };
